import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Text } from 'components/core/Typography';
import { Stars } from 'components/review/Stars';
import DefaultReviewLabel from './DefaultReviewLabel';

import './starReview.scss';

/**
 * Renders a review count with stars.
 */
const _getRatingDisplay = (rating = 0) => {
	let display = parseFloat(rating ? rating.toFixed(2) : 0);
	if (display % 1 === 0) {
		display = display.toFixed();
	}
	return `${display}`;
};

export function StarReview({
	rating,
	reviewCount,
	showRating,
	outOf,
	labelSize,
	starSize,
	starTextSize,
	showOutOf,
	className,
	labelComponent = DefaultReviewLabel,
	linkedLabel,
	color,
}) {
	const label = !showRating
		? `${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'}`
		: rating.toFixed(1);
	const ratingDisplay = _getRatingDisplay(rating);

	return (
		<div className={classnames(className, 'star-review')}>
			<Stars
				rating={rating}
				outOf={outOf}
				size={starSize}
				textSize={starTextSize}
				showOutOf={showOutOf}
				color={color}
			/>
			<div className="star-review-text">
				{showOutOf && (
					<Text size={labelSize}>
						{ratingDisplay} out of {outOf} stars
					</Text>
				)}
				{labelComponent({ labelSize, label, linkedLabel })}
			</div>
		</div>
	);
}

StarReview.propTypes = {
	/** The review rating to render stars for */
	rating: PropTypes.number.isRequired,
	/** What the review rating is out of */
	outOf: PropTypes.number,
	/** Custom label component */
	labelComponent: PropTypes.func,
	/** Text size of the label */
	labelSize: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),
	/** If the label should be a link */
	linkedLabel: PropTypes.bool,
	/** Star size */
	starSize: PropTypes.oneOf(['tiny', 'smaller', 'small', 'medium', 'large']),
	starTextSize: PropTypes.oneOf(['tiny', 'small', 'medium', 'large']),
	/** Whether to display the "X out of 5 starts" label */
	showOutOf: PropTypes.bool,
	className: PropTypes.string,
	reviewCount: PropTypes.number,
	showRating: PropTypes.bool,
	color: PropTypes.string,
};

export default StarReview;
